import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChevronRight, Users, Award, Heart, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Signup = () => {
  const navigate = useNavigate();
  const { handleAuthResponse } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'parent',
    phone: ''
  });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Phone validation helper
  const validatePhone = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length >= 10;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setLoading(true);

    // Validate phone number is required
    if (!formData.phone || !validatePhone(formData.phone)) {
      setFieldErrors({ phone: 'Phone number is required (min 10 digits)' });
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/auth/signup`,
        formData,
        { withCredentials: true }
      );

      handleAuthResponse(response.data);
      const dashboardPath = getDashboardPath(response.data.user.role);
      navigate(dashboardPath, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSignup = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const getRoleIcon = (role) => {
    switch(role) {
      case 'parent': return <Users className="w-4 h-4" />;
      case 'coach': return <Award className="w-4 h-4" />;
      case 'volunteer': return <Heart className="w-4 h-4" />;
      default: return <Users className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="signup-page">
      {/* Left side - Hero Image & Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Field Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('/field-bg-1.jpg')`,
          }}
        />
        {/* Light Navy Gradient Overlay (right to left, 5% to 1%) */}
        <div className="absolute inset-0 bg-gradient-to-l from-navy/[0.01] via-navy/[0.03] to-navy/[0.05]" />
        {/* Base navy overlay for text readability */}
        <div className="absolute inset-0 bg-navy/60" />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Top - Logo */}
          <div>
            <img 
              src="/chiefs-logo-dark.png" 
              alt="Park City Chiefs" 
              className="w-48 h-auto object-contain drop-shadow-2xl"
            />
          </div>
          
          {/* Center - Main Text */}
          <div className="flex-1 flex flex-col justify-center">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-4">
              Start Your Journey
            </p>
            <h1 className="font-display text-6xl xl:text-7xl font-black text-white uppercase tracking-tighter leading-none mb-6">
              JOIN THE<br />
              <span className="text-gold">TRIBE</span>
            </h1>
            <p className="text-white/70 text-lg max-w-md leading-relaxed font-body">
              Be part of something bigger. Join the Park City Chiefs family and help build champions.
            </p>
          </div>
          
          {/* Bottom - Stats */}
          <div className="grid grid-cols-3 gap-6">
            <div>
              <p className="font-display text-4xl font-black text-gold">
                500+
              </p>
              <p className="text-white/50 text-xs uppercase tracking-wider mt-1 font-body">Athletes</p>
            </div>
            <div>
              <p className="font-display text-4xl font-black text-gold">
                50+
              </p>
              <p className="text-white/50 text-xs uppercase tracking-wider mt-1 font-body">Coaches</p>
            </div>
            <div>
              <p className="font-display text-4xl font-black text-gold">
                10+
              </p>
              <p className="text-white/50 text-xs uppercase tracking-wider mt-1 font-body">Years</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Signup Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-[#F4F6F9] overflow-y-auto">
        <div className="w-full max-w-md py-8">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <img 
                src="/chiefs-logo.png" 
                alt="Park City Chiefs" 
                className="w-48 h-auto object-contain"
              />
            </div>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <div className="mb-6">
              <h2 className="font-display text-2xl font-black text-navy uppercase tracking-tighter mb-1">
                Create Account
              </h2>
              <p className="text-gray-500 text-sm font-body">Join the Park City Chiefs family</p>
            </div>

            {error && (
              <Alert variant="destructive" className="mb-4 border-red/30 bg-red/5" data-testid="signup-error-alert">
                <AlertDescription data-testid="signup-error-message" className="text-red font-medium">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSignup} className="space-y-4" data-testid="signup-form">
              <div>
                <Label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-gray-600">
                  Full Name
                </Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  data-testid="signup-name-input"
                  className="mt-2 h-12 bg-gray-50 border-gray-200 rounded-lg focus:border-light-blue focus:ring-2 focus:ring-light-blue/20 transition-all duration-200"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-gray-600">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                  data-testid="signup-email-input"
                  className="mt-2 h-12 bg-gray-50 border-gray-200 rounded-lg focus:border-light-blue focus:ring-2 focus:ring-light-blue/20 transition-all duration-200"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider text-gray-600">
                  Password
                </Label>
                <div className="relative mt-2">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    required
                    data-testid="signup-password-input"
                    className="h-12 bg-gray-50 border-gray-200 rounded-lg focus:border-light-blue focus:ring-2 focus:ring-light-blue/20 transition-all duration-200 pr-11"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    data-testid="signup-toggle-password"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="phone" className="text-xs font-bold uppercase tracking-wider text-gray-600">
                  Phone Number <span className="text-red">*</span>
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="(203) 555-0100"
                  value={formData.phone}
                  onChange={(e) => {
                    handleChange('phone', e.target.value);
                    if (fieldErrors.phone) setFieldErrors({});
                  }}
                  required
                  data-testid="signup-phone-input"
                  className={`mt-2 h-12 bg-gray-50 border-gray-200 rounded-lg focus:border-light-blue focus:ring-2 focus:ring-light-blue/20 transition-all duration-200 ${fieldErrors.phone ? 'border-red focus:border-red focus:ring-red/20' : ''}`}
                />
                {fieldErrors.phone && (
                  <p className="text-red text-xs mt-1 font-medium" data-testid="phone-error">{fieldErrors.phone}</p>
                )}
              </div>

              <div>
                <Label htmlFor="role" className="text-xs font-bold uppercase tracking-wider text-gray-600">
                  I Am A...
                </Label>
                <Select value={formData.role} onValueChange={(value) => handleChange('role', value)}>
                  <SelectTrigger 
                    className="mt-2 h-12 bg-gray-50 border-gray-200 rounded-lg focus:border-light-blue focus:ring-2 focus:ring-light-blue/20" 
                    data-testid="signup-role-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parent">
                      <div className="flex items-center">
                        <Users className="w-4 h-4 mr-2 text-navy" />
                        Parent / Guardian
                      </div>
                    </SelectItem>
                    <SelectItem value="coach">
                      <div className="flex items-center">
                        <Award className="w-4 h-4 mr-2 text-navy" />
                        Coach
                      </div>
                    </SelectItem>
                    <SelectItem value="volunteer">
                      <div className="flex items-center">
                        <Heart className="w-4 h-4 mr-2 text-navy" />
                        Volunteer
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-red hover:bg-red/90 text-white font-bold uppercase tracking-wider rounded-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 mt-6"
                data-testid="signup-submit-button"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating Account...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    Join The Tribe
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </span>
                )}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="px-3 bg-white text-gray-400 font-medium tracking-wider">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleSignup}
              className="w-full h-12 border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 rounded-lg font-medium transition-all duration-200"
              data-testid="google-signup-button"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Google
            </Button>
          </div>

          {/* Sign in link */}
          <p className="mt-6 text-center text-sm text-gray-600">
            Already part of the tribe?{' '}
            <Link 
              to="/login" 
              className="text-navy font-bold hover:text-red transition-colors duration-200" 
              data-testid="login-link"
            >
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

function getDashboardPath(role) {
  if (role === 'super_admin' || role === 'league_admin') {
    return '/admin/dashboard';
  } else if (role === 'coach' || role === 'team_staff') {
    return '/coach/dashboard';
  } else {
    return '/dashboard';
  }
}

export default Signup;
